import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import { Col, Form, Row, Button, notification, Spin, Switch, Tag } from "antd";
import FilterSearch from "../components/filters/FilterSearch";
import FilterChip from "../components/filters/FilterChip";
import { FOCUS_RING, cx } from "../components/filters/tokens";
import { Materia, Questao } from "../../sdk/@types";
import { Acertei } from "../../sdk/@types/Acertei";
import MateriaService from "../../sdk/services/MateriaService";
import Table from "../components/Table/Table";
import useMaterias from "../../core/hooks/UseMaterias";
import { Column, useTable } from "react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import useQuestoes from "../../core/hooks/UseQuestoes";
import { useNavigate } from "react-router-dom";
import QuestaoService from "../../sdk/services/QuestaoService";
import QuestionSourceService from "../../sdk/services/QuestionSourceService";
import useUsuario from "../../core/hooks/UseUsuario";
import QuestionPreviewModal, {
  QuestionPreviewData,
} from "../components/QuestionPreview/QuestionPreviewModal";

// Sentinela para "Sem prova" no <select> — undefined já significa "sem recorte".
const SEM_PROVA = "__none__";

export default function QuestionList() {
  const [form] = Form.useForm<Materia.Materia>();
  usePageTitle("Questões");
  const { materias, fetchMaterias } = useMaterias();
  const { questoes, buscarTodasAsQuestoes, fetching } = useQuestoes();
  const navigate = useNavigate();
  const { usuario, getUser } = useUsuario();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<QuestionPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [busca, setBusca] = useState("");
  const [materiaFilter, setMateriaFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<"todas" | "ativas" | "desativadas">("todas");
  const [apenasMinhas, setApenasMinhas] = useState(false);
  const [comImagem, setComImagem] = useState(false);
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [provaFilter, setProvaFilter] = useState<string | undefined>();
  const [provas, setProvas] = useState<Acertei.definitions["QuestionSource"][]>([]);
  const [provasLoading, setProvasLoading] = useState(true);
  const [provasError, setProvasError] = useState(false);

  // Lista curta de curadoria: buscada na montagem, não no primeiro filtro —
  // o combobox precisa estar populado antes de disparar a carga adiada das
  // questões. Falha aqui não pode derrubar a tela.
  useEffect(() => {
    QuestionSourceService.list()
      .then((list) => setProvas(list ?? []))
      .catch(() => setProvasError(true))
      .finally(() => setProvasLoading(false));
  }, []);

  // GET /questions devolve o banco inteiro e deixava a entrada da página
  // lenta. A carga agora é adiada: só dispara quando o usuário mexe em algum
  // filtro. Se o Redux já tem a lista (visita anterior), aproveita.
  const [loaded, setLoaded] = useState(questoes.length > 0);
  const temFiltro =
    busca.trim() !== "" ||
    !!materiaFilter ||
    statusFilter !== "todas" ||
    apenasMinhas ||
    comImagem ||
    !!dataDe ||
    !!dataAte ||
    !!provaFilter;

  useEffect(() => {
    if (temFiltro && !loaded) {
      setLoaded(true);
      buscarTodasAsQuestoes();
    }
  }, [temFiltro, loaded, buscarTodasAsQuestoes]);

  // Entrou na página já com lista em cache: mostra o cache na hora e
  // revalida por trás — questão criada/editada em outra tela aparecia
  // só depois de um F5.
  useEffect(() => {
    if (loaded) buscarTodasAsQuestoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // subjectId pode vir como hex ou como objeto Materia populado — normaliza.
  const subjectIdOf = useCallback((q: Questao.Questao): string => {
    const s = q.subjectId as unknown;
    if (typeof s === "string") return s;
    if (s && typeof s === "object") return String((s as Materia.Materia).id ?? "");
    return "";
  }, []);

  const materiaNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of materias) map.set(String(m.id ?? ""), m.name ?? "");
    return map;
  }, [materias]);

  // Adapta uma linha da lista para o formato que o QuestionPreviewModal
  // espera — mesma lógica de tags que o CarrosselForm usa (keyword por
  // vírgula), já que GET /questions devolve subject/testingAgency populados.
  const buildPreviewData = useCallback(
    (row: Questao.Questao): QuestionPreviewData => ({
      statement: row.statement ?? "",
      choices: (row.choices ?? []).map((c) => ({
        description: c.description ?? "",
        correct: c.correct ?? false,
        rationale: c.rationale ?? "",
      })),
      subjectName: materiaNameById.get(subjectIdOf(row)) || row.subject?.name,
      agencyName: row.testingAgency?.name,
      agencyYear: row.testingAgency?.year,
      tags: (row.keyword ?? "")
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean),
      disabled: Boolean(row.disabled),
      sharedTextId: row.sharedTextId || undefined,
    }),
    [materiaNameById, subjectIdOf]
  );

  const openPreview = useCallback(
    (row: Questao.Questao) => {
      setPreviewData(buildPreviewData(row));
      setPreviewOpen(true);
    },
    [buildPreviewData]
  );

  const closePreview = useCallback(() => setPreviewOpen(false), []);

  // userId, como subjectId, pode vir hex ou populado.
  const userIdOf = useCallback((q: Questao.Questao): string => {
    const u = q.userId as unknown;
    if (typeof u === "string") return u;
    if (u && typeof u === "object") return String((u as { id?: string }).id ?? "");
    return "";
  }, []);

  // sourceId é sempre string; source, quando presente, já vem hidratado.
  const sourceIdOf = useCallback((q: Questao.Questao): string => {
    if (q.sourceId) return String(q.sourceId);
    if (q.source && typeof q.source === "object") return String(q.source.id ?? "");
    return "";
  }, []);

  const questoesFiltradas = useMemo(() => {
    const texto = busca.trim().toLowerCase();
    const meuId = String(usuario?.id ?? "");
    // Fronteiras como ISO-prefixo: createdAt é ISO-8601, então comparação
    // lexicográfica por dia funciona sem parse de fuso.
    const de = dataDe ? `${dataDe}T00:00:00` : "";
    const ate = dataAte ? `${dataAte}T23:59:59.999` : "";
    return questoes.filter((q) => {
      if (statusFilter === "ativas" && q.disabled) return false;
      if (statusFilter === "desativadas" && !q.disabled) return false;
      if (materiaFilter && subjectIdOf(q) !== materiaFilter) return false;
      if (provaFilter) {
        const origem = sourceIdOf(q);
        if (provaFilter === SEM_PROVA) {
          if (origem) return false;
        } else if (origem !== provaFilter) {
          return false;
        }
      }
      if (apenasMinhas && (!meuId || userIdOf(q) !== meuId)) return false;
      if (comImagem && !(q.attachment ?? "").trim()) return false;
      if (de || ate) {
        const criada = q.createdAt ?? "";
        // Zero value do Mongo ("0001-01-01...") = data desconhecida: fica de
        // fora de qualquer recorte por data em vez de fingir ser antiga.
        if (!criada || criada.startsWith("0001-")) return false;
        if (de && criada < de) return false;
        if (ate && criada > ate) return false;
      }
      if (texto) {
        const alvo = `${q.id ?? ""} ${q.statement ?? ""} ${q.keyword ?? ""}`.toLowerCase();
        if (!alvo.includes(texto)) return false;
      }
      return true;
    });
  }, [
    questoes,
    busca,
    materiaFilter,
    provaFilter,
    statusFilter,
    apenasMinhas,
    comImagem,
    dataDe,
    dataAte,
    usuario?.id,
    subjectIdOf,
    userIdOf,
    sourceIdOf,
  ]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    fetchMaterias();
  }, [fetchMaterias]);

  const handleSetDisabled = useCallback(
    async (questao: Questao.Questao, disabled: boolean) => {
      if (usuario?.profile !== "master" && usuario?.profile !== "administrador") return;
      const id = String(questao.id ?? "");
      if (!id) return;

      try {
        setUpdatingId(id);
        await QuestaoService.setQuestaoDisabled(id, disabled);

        notification.success({
          message: "Sucesso",
          description: disabled
            ? "Questão desativada. Ela não aparecerá para usuários que não sejam master/administrador."
            : "Questão ativada.",
        });

        buscarTodasAsQuestoes();
      } catch {
        notification.error({
          message: "Erro",
          description: "Não foi possível ativar/desativar a questão.",
        });
      } finally {
        setUpdatingId(null);
      }
    },
    [buscarTodasAsQuestoes, usuario?.profile]
  );

  const columns: Column<Questao.Questao>[] = useMemo(() => {
    const cols: Column<Questao.Questao>[] = [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Enunciado",
        accessor: "statement",
      },
      {
        Header: "Matéria",
        Cell: ({ row }) => {
          const nome = materiaNameById.get(subjectIdOf(row.original));
          return nome ? <Tag>{nome}</Tag> : "-";
        },
      },
      {
        Header: "Prova",
        Cell: ({ row }) => row.original.source?.description || "—",
      },
      {
        Header: "Inserida em",
        Cell: ({ row }) => {
          const criada = row.original.createdAt ?? "";
          if (!criada || criada.startsWith("0001-")) return "-";
          return new Date(criada).toLocaleDateString("pt-BR");
        },
      },
      {
        Header: "Status",
        Cell: ({ row }) => {
          const q = row.original;
          const disabled = Boolean(q.disabled);

          return (
            <Tag color={disabled ? "red" : "green"}>
              {disabled ? "DESATIVADA" : "ATIVA"}
            </Tag>
          );
        },
      },
      {
        Header: "Ativar/Desativar",
        Cell: ({ row }) => {
          const q = row.original;
          const id = String(q.id ?? "");
          const disabled = Boolean(q.disabled);
          const canManage = usuario?.profile === "master" || usuario?.profile === "administrador";

          if (!canManage) return "-";

          return (
            <Switch
              checked={!disabled}
              checkedChildren="Ativa"
              unCheckedChildren="Desativada"
              loading={updatingId === id}
              onChange={(checked) => handleSetDisabled(q, !checked)}
            />
          );
        },
      },
      {
        Header: "Ação",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button
              type="primary"
              onClick={() => navigate("/questao", { state: { questao: row.original } })}
            >
              Ver Detalhes
            </Button>
            <Button onClick={() => openPreview(row.original)}>
              Ver como no carrossel
            </Button>
          </div>
        ),
      },
    ];

    return cols;
  }, [
    handleSetDisabled,
    navigate,
    updatingId,
    usuario?.profile,
    materiaNameById,
    subjectIdOf,
    openPreview,
  ]);

  const instance = useTable<Questao.Questao>({
    columns,
    data: questoesFiltradas,
  });

  return (
    <DefaultLayout>
      <div>
        <Form
          form={form}
          onFinish={async (materia: Materia.Materia) => {
            try {
              await MateriaService.inserirNovaMateria(materia);

              notification.success({
                message: "Sucesso",
                description: "Matéria cadastrada com sucesso.",
              });

              fetchMaterias();
              form.resetFields();
            } catch (error) {
              /* if (error instanceof CustomError) {
                if (error.data?.objects) {
                  form.setFields(
                    error.data.objects.map((error) => {
                      return {
                        name: error.name?.split(
                          '.'
                        ) as string[],
                        errors: [error.userMessage],
                      };
                    })
                  );
                }
              } else {
                notification.error({
                  message: 'Houve um erro',
                });
              }*/
              notification.error({
                message: "Houve um erro",
              });
            }
          }}
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="mt-5 rounded-card border border-edge bg-surface px-3 py-3 shadow-panel sm:px-4">
            <div className="flex flex-wrap items-center gap-3">
              <FilterSearch
                value={busca}
                onChange={setBusca}
                placeholder="Buscar por enunciado, ID ou tag…"
                ariaLabel="Buscar questões por enunciado, ID ou tag"
                className="min-w-[220px] flex-1"
              />
              <select
                value={materiaFilter ?? ""}
                onChange={(e) => setMateriaFilter(e.target.value || undefined)}
                aria-label="Filtrar por matéria"
                className={cx(
                  "h-10 w-full rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
                  "transition-colors duration-150 hover:border-edge-strong sm:w-56",
                  FOCUS_RING,
                )}
              >
                <option value="">Todas as matérias</option>
                {materias.map((m) => (
                  <option key={String(m.id ?? "")} value={String(m.id ?? "")}>
                    {m.name ?? ""}
                  </option>
                ))}
              </select>
              <select
                value={provaFilter ?? ""}
                onChange={(e) => setProvaFilter(e.target.value || undefined)}
                aria-label="Filtrar por prova"
                disabled={provasLoading || provasError || provas.length === 0}
                className={cx(
                  "h-10 w-full rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
                  "transition-colors duration-150 hover:border-edge-strong sm:w-56",
                  FOCUS_RING,
                )}
              >
                {provasLoading ? (
                  <option value="">Carregando provas…</option>
                ) : provasError ? (
                  <option value="">Não foi possível carregar as provas</option>
                ) : provas.length === 0 ? (
                  <option value="">Nenhuma prova cadastrada</option>
                ) : (
                  <>
                    <option value="">Todas as provas</option>
                    <option value={SEM_PROVA}>Sem prova</option>
                    {provas.map((p) => (
                      <option key={String(p.id ?? "")} value={String(p.id ?? "")}>
                        {p.description ?? ""}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                aria-label="Filtrar por status"
                className={cx(
                  "h-10 w-full rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
                  "transition-colors duration-150 hover:border-edge-strong sm:w-40",
                  FOCUS_RING,
                )}
              >
                <option value="todas">Todas</option>
                <option value="ativas">Ativas</option>
                <option value="desativadas">Desativadas</option>
              </select>
              <FilterChip
                label="Inseridas por mim"
                selected={apenasMinhas}
                onToggle={() => setApenasMinhas((v) => !v)}
              />
              <FilterChip
                label="Com imagem"
                selected={comImagem}
                onToggle={() => setComImagem((v) => !v)}
              />
              <div className="flex items-center gap-2">
                <label htmlFor="questoes-data-de" className="text-[13px] text-ink-muted">
                  De
                </label>
                <input
                  id="questoes-data-de"
                  type="date"
                  value={dataDe}
                  max={dataAte || undefined}
                  onChange={(e) => setDataDe(e.target.value)}
                  aria-label="Inseridas a partir de"
                  className={cx(
                    "h-10 rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
                    "transition-colors duration-150 hover:border-edge-strong",
                    FOCUS_RING,
                  )}
                />
                <label htmlFor="questoes-data-ate" className="text-[13px] text-ink-muted">
                  até
                </label>
                <input
                  id="questoes-data-ate"
                  type="date"
                  value={dataAte}
                  min={dataDe || undefined}
                  onChange={(e) => setDataAte(e.target.value)}
                  aria-label="Inseridas até"
                  className={cx(
                    "h-10 rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
                    "transition-colors duration-150 hover:border-edge-strong",
                    FOCUS_RING,
                  )}
                />
              </div>
              <span className="ml-auto shrink-0 text-[13px] tabular-nums text-ink-muted">
                {loaded ? `${questoesFiltradas.length} de ${questoes.length}` : "—"}
              </span>
            </div>
          </div>
          <Row gutter={16}>
            <Col span={24} style={{ marginTop: "20px" }}>
              {!loaded ? (
                <div className="rounded-card border border-dashed border-edge bg-surface px-6 py-14 text-center shadow-panel">
                  <p className="text-[15px] font-medium text-ink">
                    Use os filtros acima para carregar as questões
                  </p>
                  <p className="mt-1 text-[13px] text-ink-muted">
                    Escolha uma matéria, um status, um período ou digite uma
                    busca — a lista carrega na primeira escolha.
                  </p>
                </div>
              ) : (
                <Spin spinning={fetching} tip="Carregando questões…">
                  <Table instance={instance} />
                </Spin>
              )}
            </Col>
          </Row>
        </Form>
        <QuestionPreviewModal open={previewOpen} onCancel={closePreview} data={previewData} />
      </div>
    </DefaultLayout>
  );
}
